import React, { useCallback, useState } from 'react';
import { useStyletron } from 'baseui';
import { KIND as TagKind, Tag } from 'baseui/tag';
import { LabelSmall } from 'baseui/typography';
import { useAtomValue, useSetAtom } from 'jotai';
import useAnalyticsForSidePanel from '../../../../../common/side-panel/analytics/useAnalyticsForSidePanel';
import { type ConfigT } from '../../../../../common/types/types';
import { isQuestionnaireUpdated } from '../../../../../common/utils/utils';
import { type ModernizedGenericAnnotation, type SidePanelDispatchAtom, LabelQuestionnaire } from '../../../../../modernization-common/side-panel';
import { questionnaireDetailsMapAtom } from '../../../../../modernization-common/side-panel/atoms/questionnaire.atom';
import { SIDE_PANEL_ACTIONS } from '../../../../../modernization-common/side-panel/const';
import { SidePanelAccordion } from '../../../../../modernization-common/side-panel-m4';
import type { AnnotationT } from '../../../../labelling/types';
import { annotationLabelsAtom, annotationMetadataAtom, annotationUIStateAtom, isVideoTaskAtom } from '../../../atoms/annotation-atoms';
import { relationshipLabelsForSourceAtom, selectedAnnotationRelationshipsAtom } from '../../../atoms/relationship-atoms';
import { annotationIdToSpanAtom, staticAndDynamicQuestionnairePendingAtom } from '../../../atoms/side-panel-atoms';
import { selectedAnnotationIdsAtom } from '../../../atoms/tool-atoms';
import { useEditAnnotationForm } from '../../side-panel/hooks/use-edit-annotation-form';
import type { SelectedAnnotationDetailsT } from '../../side-panel/types';
import { InstancesPanel } from '../instances-panel/instances-panel';
import { RelationshipsPanel } from '../relationships-panel/relationships-panel';
import { containerStyles, tagOverrides } from './edit-panel-overrides';
import { getAutoExpandedPanels, getMetadataQuestionnaireTagLabel, getQuestionnaireEventPayload, getSelectedAnnotationDetails, getUpdatedAnnotationDetailsWithQuestionnaireAnswers, PANEL_KEYS, shouldPushCollapsedPanelToBottom } from './utils';
export { PANEL_KEYS } from './utils';
export type EditAnnotationsPanelPropsT = {
  defaultExpandedPanels?: React.Key[];
};
type EditAnnotationsPanelProps = {
  onClose?: (data?: unknown) => void;
  defaultExpandedPanels?: React.Key[];
  dispatchAtom: SidePanelDispatchAtom<AnnotationT & ModernizedGenericAnnotation>;
  onValidateAnswers?: (annotationId: string, answers: any) => Promise<any[]>;
  config?: ConfigT;
};
const TagAndLabelRenderer = ({
  label,
  tagLabel,
  tagKind,
  children
}: {
  label: string;
  tagLabel?: string;
  tagKind?: (typeof TagKind)[keyof typeof TagKind];
  children?: React.ReactNode;
}) => {
  const [css, theme] = useStyletron();
  return <div className={css({
    ...containerStyles,
    justifyContent: 'space-between',
    width: '100%',
    marginRight: theme.sizing.scale500
  })}>
      <div className={css({
      ...containerStyles,
      gap: theme.sizing.scale300
    })}>
        <LabelSmall>{label}</LabelSmall>
        {tagLabel && <Tag kind={tagKind} overrides={tagOverrides} closeable={false}>
            {tagLabel}
          </Tag>}
      </div>
      {children}
    </div>;
};
export const EditAnnotationsPanel = ({
  onClose,
  defaultExpandedPanels,
  dispatchAtom,
  config
}: EditAnnotationsPanelProps) => {
  const [css, theme] = useStyletron();
  const annotationLabels = useAtomValue(annotationLabelsAtom);
  const selectedAnnotationId = useAtomValue(selectedAnnotationIdsAtom)[0];
  const annotationMetadata = useAtomValue(annotationMetadataAtom);
  const annotationUIState = useAtomValue(annotationUIStateAtom);
  const questionnaireDetailsMap = useAtomValue(questionnaireDetailsMapAtom);
  const selectedAnnotationDetails = getSelectedAnnotationDetails({
    selectedAnnotationId,
    annotationLabels,
    annotationMetadata,
    annotationUIState
  });
  const staticAndDynamicQuestionnairePending = useAtomValue(staticAndDynamicQuestionnairePendingAtom);
  const dispatch = useSetAtom(dispatchAtom);
  const {
    questionnaireAnswers,
    selectedLabel,
    hasQuestionnaire,
    getSelectedLabelQuestionnaireId,
    handleQuestionnaireChange
  } = useEditAnnotationForm({
    selectedAnnotationDetails,
    annotationLabels
  });
  const {
    sendEventForQuestionnaireUpdated,
    sendEventForQuestionnaireAnswered
  } = useAnalyticsForSidePanel();
  const isStaticQuestionnairePending = (hasQuestionnaire && staticAndDynamicQuestionnairePending[selectedAnnotationDetails?.id || '']?.staticQuestionnairePending) ?? false;
  const selectedQuestionnaire = questionnaireDetailsMap[getSelectedLabelQuestionnaireId()];
  const metadataTagLabel = getMetadataQuestionnaireTagLabel({
    isStaticQuestionnairePending,
    questionnaireAnswers,
    selectedQuestionnaire
  });
  const handleUpdateAnnotation = useCallback((annotation: SelectedAnnotationDetailsT) => {
    dispatch({
      type: SIDE_PANEL_ACTIONS.UPDATE_ANNOTATION_QUESTIONNAIRE,
      annotationIdx: annotation.id,
      answers: annotation.metadata?.answers ?? {}
    });
  }, [dispatch]);
  const annotationIdToSpan = useAtomValue(annotationIdToSpanAtom);
  const isVideoTask = useAtomValue(isVideoTaskAtom);
  const sourceEligibleRelationshipLabels = useAtomValue(relationshipLabelsForSourceAtom);
  const canSelectedAnnotationSourceRelationship = sourceEligibleRelationshipLabels.length > 0;
  const selectedAnnotationRelationships = useAtomValue(selectedAnnotationRelationshipsAtom);
  const rangeSpans = annotationIdToSpan[selectedAnnotationDetails?.id || ''];
  const hasInstances = isVideoTask && !!rangeSpans && rangeSpans.length > 0;
  const autoExpandedPanels = getAutoExpandedPanels({
    defaultExpandedPanels,
    hasQuestionnaire,
    isVideoTask
  });
  const [userExpandedPanel, setUserExpandedPanel] = useState<React.Key[] | undefined>();
  const expandedPanel = userExpandedPanel ?? autoExpandedPanels;
  const visiblePanelKeys = [...(hasQuestionnaire ? [PANEL_KEYS.METADATA] : []), ...(isVideoTask ? [PANEL_KEYS.INSTANCES] : []), ...(canSelectedAnnotationSourceRelationship ? [PANEL_KEYS.RELATIONSHIPS] : [])];
  const handleQuestionnaireSave = (updatedAnnotationDetails: SelectedAnnotationDetailsT, selectedAnnotationDetails: SelectedAnnotationDetailsT) => {
    handleUpdateAnnotation(updatedAnnotationDetails);
    const questionnaireUpdated = isQuestionnaireUpdated(selectedAnnotationDetails, updatedAnnotationDetails);
    const questionnaireEventPayload = getQuestionnaireEventPayload(updatedAnnotationDetails);
    questionnaireUpdated ? sendEventForQuestionnaireUpdated(questionnaireEventPayload) : !selectedAnnotationDetails.metadata?.answers && sendEventForQuestionnaireAnswered(questionnaireEventPayload);
  };
  if (!selectedAnnotationDetails) {
    onClose?.();
    return null;
  }
  const accordionSections = [...(hasQuestionnaire ? [{
    id: PANEL_KEYS.METADATA,
    header: <TagAndLabelRenderer label="Metadata" tagLabel={metadataTagLabel} tagKind={isStaticQuestionnairePending ? TagKind.warning : TagKind.positive} />,
    children: <LabelQuestionnaire style={{
      paddingTop: theme.sizing.scale0,
      paddingBottom: theme.sizing.scale0
    }} key={`${getSelectedLabelQuestionnaireId()}:${selectedLabel}`} questionnaireId={getSelectedLabelQuestionnaireId()} answers={questionnaireAnswers || {}} onChange={(value, answers) => {
      handleQuestionnaireChange(value, answers);
      const updatedAnnotationDetails = getUpdatedAnnotationDetailsWithQuestionnaireAnswers({
        selectedAnnotationDetails,
        answers
      });
      handleQuestionnaireSave(updatedAnnotationDetails, selectedAnnotationDetails);
    }} isQuestionnaireComposable={config?.useComposableQuestionnaire} />
  }] : []), ...(isVideoTask ? [{
    id: PANEL_KEYS.INSTANCES,
    header: <TagAndLabelRenderer label="Instances" tagLabel={String(hasInstances ? rangeSpans?.length : 0)} tagKind={TagKind.accent} />,
    children: <InstancesPanel isTracked={hasInstances} annotationId={selectedAnnotationDetails.id} dispatchAtom={dispatchAtom} />,
    pushToBottom: shouldPushCollapsedPanelToBottom({
      panelKey: PANEL_KEYS.INSTANCES,
      visiblePanelKeys,
      expandedPanelKeys: expandedPanel
    })
  }] : []), ...(canSelectedAnnotationSourceRelationship ? [{
    id: PANEL_KEYS.RELATIONSHIPS,
    header: <TagAndLabelRenderer label="Relationships" tagLabel={String(selectedAnnotationRelationships.length)} tagKind={TagKind.accent} />,
    children: <RelationshipsPanel />,
    pushToBottom: shouldPushCollapsedPanelToBottom({
      panelKey: PANEL_KEYS.RELATIONSHIPS,
      visiblePanelKeys,
      expandedPanelKeys: expandedPanel
    })
  }] : [])];
  return <div className={css({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  })}>
      <section className={css({
      flex: 1,
      minHeight: 0,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    })}>
        <SidePanelAccordion accordion sections={accordionSections} expandedKeys={expandedPanel} onChange={setUserExpandedPanel} />
      </section>
    </div>;
};